import { Button } from "@somethings-ui/web";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface AccountFieldProps {
  label: string;
  value: string;
  valueContent?: ReactNode;
  hint?: string;
  open: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onSave?: () => void | Promise<void>;
  saving?: boolean;
  saveLabel?: string;
  cancelAsText?: boolean;
  readOnly?: boolean;
  children?: ReactNode;
}

export function AccountField({
  label,
  value,
  valueContent,
  hint,
  open,
  onOpen,
  onCancel,
  onSave,
  saving = false,
  saveLabel = "Save",
  cancelAsText = false,
  readOnly = false,
  children,
}: AccountFieldProps) {
  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(7rem,10.5rem)_1fr] gap-6 border-b border-[#dfddd8] px-6 py-3.5 last:border-b-0",
        open ? "items-start" : "min-h-[52px] items-center",
      )}
    >
      <p className="pt-0.5 font-body text-xs font-medium tracking-[0.2px] text-[#6e6b67]">
        {label}
      </p>
      <div className="flex min-w-0 items-center gap-4">
        <div className="min-w-0 flex-1">
          {open ? (
            <>
              {children}
              {onSave ? (
                <div className="mt-4 flex items-center gap-3">
                  <Button
                    size="sm"
                    fullWidth={false}
                    className="min-w-[120px]"
                    loading={saving}
                    disabled={saving}
                    onPress={() => {
                      void Promise.resolve(onSave())
                        .then(() => onCancel())
                        .catch(() => undefined);
                    }}
                  >
                    {saveLabel}
                  </Button>
                  {cancelAsText ? (
                    <button
                      type="button"
                      className="font-body text-sm font-bold text-text-primary"
                      disabled={saving}
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      fullWidth={false}
                      className="min-w-[120px]"
                      disabled={saving}
                      onPress={onCancel}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              ) : null}
            </>
          ) : valueContent ? (
            valueContent
          ) : (
            <p className="truncate font-body text-[15px] leading-[22.5px] text-text-primary">
              {value || "—"}
              {hint ? (
                <span className="text-xs text-[#6e6b67]">{` · ${hint}`}</span>
              ) : null}
            </p>
          )}
        </div>
        {open || readOnly ? null : (
          <button
            type="button"
            onClick={onOpen}
            className="shrink-0 rounded-full px-4 py-2 font-body text-xs font-bold text-text-primary"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

export function AccountSectionCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[16px] border border-[#dfddd8] bg-white py-1.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function AccountChoiceChip({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "h-8 rounded-full px-3 font-body text-sm",
        selected
          ? "bg-text-primary text-white"
          : "border border-[#dfddd8] text-text-primary",
      )}
    >
      {label}
    </button>
  );
}
